<template>
    <div class="session-duration-chart" :class="{ fullscreen: fullScreen }">
        <h2 v-if="fullScreen">{{ $t('report.sessionDurationOverall') }}</h2>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton :classNames="tableMode ? '' : 'selected'" :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }">
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton :classNames="tableMode ? 'selected' : ''" :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }">
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="middle">
                <label>{{ $t("report.aggregateAs") }}:</label>
                <a-radio-group size="small" v-model="aggregationType">
                    <a-radio-button value="avg"> {{ $t("message.average") }}</a-radio-button>
                    <a-radio-button value="total"> {{ $t("report.total") }}</a-radio-button>
                </a-radio-group>
                <label>{{ $t("report.handledBy") }}</label>
                <a-radio-group size="small" v-model="handleType">
                    <a-radio-button value="hybrid">{{ $t("report.hybrid") }}</a-radio-button>
                    <a-radio-button value="chatbot">{{ $t("message.chatBot") }}</a-radio-button>
                    <a-radio-button value="agent"> {{ $t("billing.agent") }}</a-radio-button>
                </a-radio-group>
            </div>
            <div class="right">
                <label>{{ $t("report.viewUnitAs") }}:</label>
                <a-radio-group size="small" v-model="unit">
                    <a-radio-button value="seconds"> {{ $t("report.second") }}</a-radio-button>
                    <a-radio-button value="minutes"> {{ $t("report.minute") }}</a-radio-button>
                    <a-radio-button value="hours"> {{ $t("report.hour") }}</a-radio-button>
                </a-radio-group>
            </div>
        </div>
        <TableView v-if="tableMode" :cols="getCols()" :rows="getRows()" />
        <EChartsLine
            v-else
            :labels="getXAxisData()"
            :datasets="getChartDatasets()"
            :height="320"
        />
        <div class="stats">
            <StatCard :title="$t('report.totalSessionDuration')" :value="humanizeDuration(stats.TotalDuration, unit)"
                :description="$t('report.totalSessionDurationDesc')"
                v-if="aggregationType === 'total'" />
            <StatCard :title="$t('report.totalDurationByCB')"
                :value="humanizeDuration(stats.ChatbotTotalDuration, unit)"
                :description="$t('report.totalDurationByCBDesc')"
                v-if="(handleType === 'chatbot' || handleType === 'hybrid') && aggregationType === 'total'" />
            <StatCard :title="$t('report.totalDurationByAgent')"
                :value="humanizeDuration(stats.AgentTotalDuration, unit)"
                :description="$t('report.totalDurationByAgentDesc')"
                v-if="(handleType === 'agent' || handleType === 'hybrid') && aggregationType === 'total'" />
            <StatCard :title="$t('report.averageSessionDuration')"
                :value="humanizeDuration(stats.AverageDuration, unit)"
                :description="$t('report.averageSessionDurationDesc')"
                v-if="aggregationType === 'avg'" />
            <StatCard :title="$t('report.averageDurationByCB')"
                :value="humanizeDuration(stats.ChatbotAverageDuration, unit)"
                :description="$t('report.averageDurationByCBDesc')"
                v-if="(handleType === 'chatbot' || handleType === 'hybrid') && aggregationType === 'avg'" />
            <StatCard :title="$t('report.averageDurationByAgent')"
                :value="humanizeDuration(stats.AgentAverageDuration, unit)"
                :description="$t('report.averageDurationByAgentDesc')"
                v-if="(handleType === 'agent' || handleType === 'hybrid') && aggregationType === 'avg'" />
            <StatCard :title="$t('report.maxDuration')" :value="humanizeDuration(stats.MaxDuration, unit)"
                :description="$t('report.maxDurationDesc')" />
            <StatCard :title="$t('report.minDuration')" :value="humanizeDuration(stats.MinDuration, unit)"
                :description="$t('report.minDurationDesc')" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import { expertToExcel } from "../../../utils/utils";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import EChartsLine from "../../../components/charts/EChartsLine.vue";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import fscreen from "fscreen";

const log = log2("OverallSessionDurationChart");
export default Vue.extend({
    name: "OverallSessionDurationChart",
    components: { IconButton, StatCard, EChartsLine, TableView },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            stats: {
                TotalDuration: 0,
                ChatbotTotalDuration: 0,
                AgentTotalDuration: 0,
                AverageDuration: 0,
                ChatbotAverageDuration: 0,
                AgentAverageDuration: 0,
                MaxDuration: 0,
                MinDuration: 0,
            },
            aggregationType: "avg",
            handleType: "hybrid",
            unit: "minutes",
            tableMode: false,
            loading: true,
            loadingInstance: null,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
            },
            fullScreen: false,
            cancels: [],
        };
    },
    methods: {
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.sessionDurationOverall(this.filters)
                .then((res) => {
                    log("sessionOverall data fetched", res);
                    this.graphData = res;
                    this.stats = this.getStats();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        humanizeDuration(valueInMs, type = 'default') {
            const conversionRates = {
                seconds: 1000,
                minutes: 60 * 1000,
                hours: 3600 * 1000,
            };

            if (type in conversionRates) {
                const convertedValue = valueInMs / conversionRates[type];

                const literals = {
                    hour: this.$i18n.locale === "tr" ? "saat" : "hour",
                    min: this.$i18n.locale === "tr" ? "dk" : "min",
                    sec: this.$i18n.locale === "tr" ? "sn" : "sec",
                };

                switch (type) {
                    case 'seconds':
                        return `${convertedValue.toFixed(1)} ${literals.sec}`;
                    case 'minutes':
                        return `${convertedValue.toFixed(1)} ${literals.min}`;
                    case 'hours':
                        return `${convertedValue.toFixed(1)} ${literals.hour}`;
                    default:
                        return `${convertedValue.toFixed(1)} ${literals.sec}`;
                }
            }
            return `${(valueInMs / 1000).toFixed(1)} sec`;
        },
        getReportTitle() {
            const titles = {
                hybrid: {
                    avg: this.$t("report.averageSessionDuration"),
                    total: this.$t("report.totalSessionDuration"),
                },
                chatbot: {
                    avg: this.$t("report.averageDurationByCB"),
                    total: this.$t("report.totalDurationByCB"),
                },
                agent: {
                    avg: this.$t("report.averageDurationByAgent"),
                    total: this.$t("report.totalDurationByAgent"),
                },
            };
            return titles[this.handleType][this.aggregationType];
        },
        getStats() {
            const { buckets } = this.graphData;
            if (!buckets || buckets.length === 0) {
                return {
                    TotalDuration: 0,
                    ChatbotTotalDuration: 0,
                    AgentTotalDuration: 0,
                    AverageDuration: 0,
                    ChatbotAverageDuration: 0,
                    AgentAverageDuration: 0,
                    MaxDuration: 0,
                    MinDuration: 0,
                };
            }

            const totals = buckets.reduce(
                (acc, bucket) => ({
                    TotalDuration: acc.TotalDuration + (bucket.totalDuration || 0),
                    ChatbotTotalDuration: acc.ChatbotTotalDuration + (bucket.chatbotTotalDuration || 0),
                    AgentTotalDuration: acc.AgentTotalDuration + (bucket.agentTotalDuration || 0),
                    TotalCount: acc.TotalCount + (bucket.count || 0),
                    MaxAvgDuration: Math.max(acc.MaxAvgDuration, bucket.avgDuration || 0),
                    MinAvgDuration: Math.min(acc.MinAvgDuration, bucket.count > 0 ? (bucket.avgDuration || 0) : Infinity),
                    MaxTotalDuration: Math.max(acc.MaxTotalDuration, bucket.totalDuration || 0),
                    MinTotalDuration: Math.min(acc.MinTotalDuration, bucket.count > 0 ? (bucket.totalDuration || 0) : Infinity),
                }),
                {
                    TotalDuration: 0,
                    ChatbotTotalDuration: 0,
                    AgentTotalDuration: 0,
                    TotalCount: 0,
                    MaxAvgDuration: -Infinity,
                    MinAvgDuration: Infinity,
                    MaxTotalDuration: -Infinity,
                    MinTotalDuration: Infinity,
                }
            );

            const totalCount = totals.TotalCount;

            return {
                TotalDuration: totals.TotalDuration,
                ChatbotTotalDuration: totals.ChatbotTotalDuration,
                AgentTotalDuration: totals.AgentTotalDuration,
                AverageDuration: totalCount > 0 ? totals.TotalDuration / totalCount : 0,
                ChatbotAverageDuration: totalCount > 0 ? totals.ChatbotTotalDuration / totalCount : 0,
                AgentAverageDuration: totalCount > 0 ? totals.AgentTotalDuration / totalCount : 0,
                MaxDuration: this.aggregationType === 'avg'
                    ? (totals.MaxAvgDuration === -Infinity ? 0 : totals.MaxAvgDuration)
                    : (totals.MaxTotalDuration === -Infinity ? 0 : totals.MaxTotalDuration),
                MinDuration: this.aggregationType === 'avg'
                    ? (totals.MinAvgDuration === Infinity ? 0 : totals.MinAvgDuration)
                    : (totals.MinTotalDuration === Infinity ? 0 : totals.MinTotalDuration),
            };
        },

        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    return moment(item.date).format("MMM");
                }
            });
        },
        readValueFromBucket(bucket, applyDurationUnit = true) {
            const unitRatio = {
                seconds: 1000,
                minutes: 60 * 1000,
                hours: 3600 * 1000,
            }[this.unit];
            const values = {
                hybrid: {
                    avg: bucket.avgDuration,
                    total: bucket.totalDuration,
                },
                chatbot: {
                    avg: bucket.avgChatbotDuration,
                    total: bucket.chatbotTotalDuration,
                },
                agent: {
                    avg: bucket.avgAgentDuration,
                    total: bucket.agentTotalDuration,
                },
            };
            const valueInMs = values[this.handleType][this.aggregationType] || 0;
            return applyDurationUnit ? valueInMs / unitRatio : valueInMs;
        },
        getChartDatasets() {
            const { buckets } = this.graphData;
            const data = buckets.map((bucket) => this.readValueFromBucket(bucket));
            return [
                {
                    label: this.getReportTitle(),
                    data,
                    fill: true,
                    backgroundColor: "rgba(168, 85, 247, 0.15)",
                    borderColor: "rgba(168, 85, 247, 1)",
                    borderWidth: 2,
                    pointRadius: 3,
                    pointBackgroundColor: "rgba(168, 85, 247, 1)",
                    tension: 0.4,
                },
            ];
        },
        getCols() {
            return [
                {
                    title: this.$t("message.date"),
                    dataIndex: "date",
                    key: "date",
                    width: 250,
                    fixed: "left",
                },
                {
                    title: this.getReportTitle(),
                    dataIndex: "value",
                    key: "value",
                    width: 100,
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            return buckets.map((bucket) => {
                const valueInMs = this.readValueFromBucket(bucket, false);
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    value: this.humanizeDuration(valueInMs, this.unit),
                };
            });
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;

            const data = buckets.map((bucket) => {
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date: date,
                    [this.$t("report.totalSessionDuration")]: this.humanizeDuration(bucket.totalDuration, this.unit),
                    [this.$t("report.averageSessionDuration")]: this.humanizeDuration(bucket.avgDuration, this.unit),
                    [this.$t("report.totalDurationByCB")]: this.humanizeDuration(bucket.chatbotTotalDuration, this.unit),
                    [this.$t("report.averageDurationByCB")]: this.humanizeDuration(bucket.avgChatbotDuration, this.unit),
                    [this.$t("report.totalDurationByAgent")]: this.humanizeDuration(bucket.agentTotalDuration, this.unit),
                    [this.$t("report.averageDurationByAgent")]: this.humanizeDuration(bucket.avgAgentDuration, this.unit),
                };
            });

            expertToExcel(data, `session_duration_timeseries.xlsx`, "Sheet 1");
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = this.$el;
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: this.$el,
            name: "wave",
        });
        this.fetch();
        const fullscreenCallback = () => {
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        aggregationType() {
            this.stats = this.getStats();
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>

<style lang="scss">
.session-duration-chart {
    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .echarts-line-wrapper {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.session-duration-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);
                border-radius: 6px;
                overflow: hidden;

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>